import { useEffect, useState } from "react";
import { useTranslation } from "@repo/i18n";
import { css } from "@repo/styled-system/css";
import {
  clearEntitlementOverride,
  setEntitlementOverride,
  venueEntitlements,
  type EntitlementView,
} from "./opsApi";
import { opsButton } from "./opsStyles";

// Every gateable feature (domain EntitlementKey). FLOOR_PLAN first — the common operator ask.
const FLAGS = [
  "FLOOR_PLAN",
  "WAITLIST",
  "SMS",
  "WHATSAPP",
  "CRM_NOTES_TAGS",
  "MULTI_SITE",
  "MEDIA",
  "CUSTOM_DOMAIN",
] as const;

const OVERRIDE_DAYS = 90;

/**
 * Per-venue feature overrides on the /ops tenant record. An operator OVERRIDE wins over plan/trial
 * (EN-1) — the way to switch a feature on or off for a customer regardless of their plan or trial
 * (e.g. FLOOR_PLAN, which a trial can never grant under R3). Reason is mandatory; overrides always
 * expire (90-day default here). "Clear" reverts a flag to its underlying trial/plan source.
 */
export function EntitlementPanel({ venueId }: { venueId: string }) {
  const { t } = useTranslation();
  const [view, setView] = useState<EntitlementView | null>(null);
  const [reason, setReason] = useState("");
  const [notice, setNotice] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);

  useEffect(() => {
    let active = true;
    venueEntitlements(venueId)
      .then((v) => active && setView(v))
      .catch(() => active && setNotice(t("ops.entitlement.error")));
    return () => {
      active = false;
    };
  }, [venueId, t]);

  const byKey = new Map((view?.flags ?? []).map((f) => [f.key, f]));

  function requireReason(): string | null {
    const r = reason.trim();
    if (!r) {
      setNotice(t("ops.entitlement.reasonRequired"));
      return null;
    }
    setNotice(null);
    return r;
  }

  async function override(flag: string, enabled: boolean) {
    const r = requireReason();
    if (!r) return;
    setBusy(flag);
    const expiresAt = new Date(Date.now() + OVERRIDE_DAYS * 86_400_000).toISOString();
    try {
      setView(await setEntitlementOverride(venueId, flag, enabled, r, expiresAt));
      setNotice(t("ops.entitlement.applied"));
    } catch {
      setNotice(t("ops.entitlement.error"));
    } finally {
      setBusy(null);
    }
  }

  async function clear(flag: string) {
    setBusy(flag);
    try {
      setView(await clearEntitlementOverride(venueId, flag));
      setNotice(t("ops.entitlement.cleared"));
    } catch {
      setNotice(t("ops.entitlement.error"));
    } finally {
      setBusy(null);
    }
  }

  return (
    <section
      data-testid="ops-entitlement-panel"
      className={css({
        mt: "4",
        p: "3",
        borderWidth: "1px",
        borderColor: "border.default",
        rounded: "md",
      })}
    >
      <h3 className={css({ fontWeight: "semibold", mb: "2" })}>{t("ops.entitlement.heading")}</h3>

      <label className={css({ display: "block", mb: "2" })}>
        <span className={css({ display: "block", fontWeight: "medium", mb: "1", fontSize: "sm" })}>
          {t("ops.entitlement.reason")}
        </span>
        <textarea
          data-testid="ops-entitlement-reason"
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          rows={2}
          className={css({
            w: "full",
            p: "2",
            borderWidth: "1px",
            borderColor: "border.default",
            rounded: "md",
          })}
        />
      </label>
      {notice && (
        <output className={css({ display: "block", mb: "2", color: "fg.muted", fontSize: "sm" })}>
          {notice}
        </output>
      )}

      <ul className={css({ display: "flex", flexDirection: "column", gap: "1" })}>
        {FLAGS.map((flag) => {
          const state = byKey.get(flag);
          const enabled = state?.enabled ?? false;
          const overridden = state?.source === "OPERATOR_OVERRIDE";
          return (
            <li
              key={flag}
              className={css({
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: "2",
                py: "1",
                flexWrap: "wrap",
              })}
            >
              <span className={css({ fontSize: "sm" })}>
                <strong>{flag}</strong>{" "}
                <span className={css({ color: enabled ? "colorPalette.default" : "fg.muted" })}>
                  {enabled ? t("ops.entitlement.on") : t("ops.entitlement.off")}
                </span>
                {state && (
                  <span className={css({ color: "fg.muted" })}>
                    {" "}
                    · {state.source}
                    {state.overrideExpiresAt
                      ? ` · ${new Date(state.overrideExpiresAt).toLocaleDateString()}`
                      : ""}
                  </span>
                )}
              </span>
              <span className={css({ display: "flex", gap: "1", flexShrink: 0 })}>
                <button
                  type="button"
                  className={opsButton}
                  disabled={busy === flag}
                  onClick={() => void override(flag, true)}
                >
                  {t("ops.entitlement.enable")}
                </button>
                <button
                  type="button"
                  className={opsButton}
                  disabled={busy === flag}
                  onClick={() => void override(flag, false)}
                >
                  {t("ops.entitlement.disable")}
                </button>
                <button
                  type="button"
                  className={opsButton}
                  disabled={busy === flag || !overridden}
                  onClick={() => void clear(flag)}
                >
                  {t("ops.entitlement.clear")}
                </button>
              </span>
            </li>
          );
        })}
      </ul>
    </section>
  );
}
